/**
 * Rules and rates (Firm Settings › Data and security): fees, a tax rate, due dates and state
 * holidays that could not be checked on their official page. Everyone on staff sees each one's
 * status; owners and admins confirm the value in use or correct it, with a source, and see the
 * history. Also the status label other screens reuse ("Not confirmed", "Confirmed by …").
 */
import { COMPLIANCE_STATES, NOT_CONFIRMED_LABEL, NOT_SET_LABEL, parseDollars, ruleSettingValueText, type RuleSettingKind, type RuleSettingStatus, type RuleSettingValue } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Empty, ListSearch, Loading, matches, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const pill = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";

/** A rule's value with its status, as the API returns it everywhere. */
export type RuleStatusView = { key: string; name: string; kind: RuleSettingKind; value: RuleSettingValue | null; status: RuleSettingStatus; confirmedBy: string | null; confirmedOn: string | null };

export function ruleStatusLabel(r: Pick<RuleStatusView, "status" | "confirmedBy" | "confirmedOn">): string {
  if (r.status === "not_set") return NOT_SET_LABEL;
  if (r.status === "not_confirmed" || !r.confirmedOn) return NOT_CONFIRMED_LABEL;
  return `Confirmed by ${r.confirmedBy ?? "a former team member"} on ${formatDate(r.confirmedOn)}`;
}

/** The status chip: amber until confirmed (waiting on the firm), emerald once confirmed. */
export function RuleStatusChip({ r, className }: { r: Pick<RuleStatusView, "status" | "confirmedBy" | "confirmedOn">; className?: string }) {
  return (
    <span className={cn(pill, r.status === "confirmed" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200", className)}>
      {ruleStatusLabel(r)}
    </span>
  );
}

/** "Fee $300 · Not confirmed" for a line under a filing or registration. */
export function RuleFigureText({ r, label }: { r: RuleStatusView; label: string }) {
  return (
    <span>
      {label}
      {r.value !== null && ` ${ruleSettingValueText(r.kind, r.value)}`}
      <span className={r.status === "confirmed" ? "text-muted-foreground" : "text-amber-700 dark:text-amber-300"}> · {ruleStatusLabel(r)}</span>
    </span>
  );
}

type HistoryRow = { id: string; action: "confirmed" | "corrected"; value: RuleSettingValue; previousValue: RuleSettingValue | null; source: string; effectiveFrom: string; byName: string | null; createdAt: string };
type Rule = RuleStatusView & {
  jurisdiction: string;
  coded: RuleSettingValue | null;
  usedIn: string;
  officialUrl: string | null;
  note: string;
  clientFacing: boolean;
  upcoming: { value: RuleSettingValue; effectiveFrom: string } | null;
  history: HistoryRow[];
};
type Data = { today: string; canEdit: boolean; rules: Rule[] };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const isUrl = (s: string) => /^https?:\/\//i.test(s.trim());

function valueText(kind: RuleSettingKind, v: RuleSettingValue | null): string {
  return v === null ? "Not set" : ruleSettingValueText(kind, v);
}

/** Confirm the value in use, or correct it from a date; both need a source. */
function ChangeDialog({ rule, mode, today, onClose }: { rule: Rule; mode: "confirm" | "correct"; today: string; onClose: () => void }) {
  const v = rule.value;
  const [amount, setAmount] = useState(rule.kind === "amount" && typeof v === "number" ? (v / 100).toFixed(2) : "");
  const [rate, setRate] = useState(rule.kind === "rate" && typeof v === "number" ? String(v) : "");
  const [month, setMonth] = useState(rule.kind === "date-rule" && v && typeof v === "object" && !Array.isArray(v) ? v.month : 1);
  const [day, setDay] = useState(rule.kind === "date-rule" && v && typeof v === "object" && !Array.isArray(v) ? v.day : 1);
  const [dates, setDates] = useState(Array.isArray(v) ? v.join("\n") : "");
  const [source, setSource] = useState(rule.officialUrl ?? "");
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [localError, setLocalError] = useState<string | null>(null);
  const save = useMutation({ meta: { inlineError: true }, mutationFn: (body: Record<string, unknown>) => api.send("POST", `/api/firm/rule-settings/${rule.key}`, body), onSuccess: onClose });
  const submit = () => {
    setLocalError(null);
    if (!source.trim()) return setLocalError("Enter the page you checked (its address) or a short note on where the figure comes from.");
    if (mode === "confirm") return save.mutate({ action: "confirm", source: source.trim() });
    let value: RuleSettingValue;
    try {
      if (rule.kind === "amount") value = parseDollars(amount);
      else if (rule.kind === "rate") {
        value = Number(rate.trim().replace(/%$/, ""));
        if (!rate.trim() || !Number.isFinite(value)) return setLocalError("Enter the rate as a percent, for example 6 or 6.25.");
      } else if (rule.kind === "date-rule") value = { month, day };
      else value = dates.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    } catch {
      return setLocalError("Enter the amount in dollars, for example 300 or 300.00.");
    }
    save.mutate({ action: "correct", value, source: source.trim(), effectiveFrom });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "confirm" ? "Confirm this rule" : rule.value === null ? "Add the value" : "Correct this rule"}</DialogTitle>
          <DialogDescription>
            {rule.name}. {mode === "confirm" ? `Confirm ${valueText(rule.kind, rule.value)} only after checking it on the official page.` : "Enter the figure or dates from the official page. It applies from the date you choose; anything due earlier, and filings already made, stay as they are."}
          </DialogDescription>
        </DialogHeader>
        {mode === "correct" && (
          <div className="grid gap-3">
            {rule.kind === "amount" && (
              <label className="text-sm">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Amount</span>
                <Input inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </label>
            )}
            {rule.kind === "rate" && (
              <label className="text-sm">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Rate (percent)</span>
                <Input inputMode="decimal" placeholder="For example 6" value={rate} onChange={(e) => setRate(e.target.value)} />
              </label>
            )}
            {rule.kind === "date-rule" && (
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Month</span>
                  <select className={cn(select, "w-full")} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Day</span>
                  <Input type="number" min={1} max={31} value={day} onChange={(e) => setDay(Number(e.target.value))} />
                </label>
              </div>
            )}
            {rule.kind === "holiday-list" && (
              <label className="text-sm">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">Holidays, one date per line (YYYY-MM-DD)</span>
                <Textarea rows={6} placeholder="2027-11-26" value={dates} onChange={(e) => setDates(e.target.value)} />
                <span className="mt-1 block text-xs text-muted-foreground">Federal holidays are already counted. List the whole set for the years you are entering.</span>
              </label>
            )}
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Applies from</span>
              <Input type="date" min={today} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            </label>
          </div>
        )}
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Source (the page's address, or a note)</span>
          <Input maxLength={1000} value={source} onChange={(e) => setSource(e.target.value)} />
        </label>
        {(localError || save.error) && <p className="text-sm text-destructive">{localError ?? errorMessage(save.error)}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {mode === "confirm" ? "Confirm rule" : rule.value === null ? "Save value" : "Save correction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function History({ rule }: { rule: Rule }) {
  if (!rule.history.length) return <p className="text-xs text-muted-foreground">Nobody has confirmed or changed this yet.</p>;
  return (
    <ol className="space-y-2 border-l-2 border-border pl-3">
      {rule.history.map((h) => (
        <li key={h.id} className="text-xs">
          <span className="font-medium">
            {h.action === "confirmed" ? "Confirmed" : "Changed"} by {h.byName ?? "a former team member"}
          </span>{" "}
          <span className="text-muted-foreground">on {formatDateTime(h.createdAt)}</span>
          <span className="block">
            {h.action === "corrected" ? `${valueText(rule.kind, h.previousValue)} → ${valueText(rule.kind, h.value)}, from ${formatDate(h.effectiveFrom)}` : valueText(rule.kind, h.value)}
            {rule.kind === "holiday-list" && Array.isArray(h.value) && h.value.length > 0 && <span className="block text-muted-foreground">{h.value.map((d) => formatDate(d)).join(", ")}</span>}
          </span>
          <span className="block break-words text-muted-foreground">
            Source:{" "}
            {isUrl(h.source) ? (
              <a href={h.source.trim()} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                {h.source}
              </a>
            ) : (
              h.source
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Firm Settings › Data and security › Rules and rates. */
export function RulesAndRatesSettings() {
  const { data, isLoading, error } = useQuery<Data>({ queryKey: ["/api/firm/rule-settings"] });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<{ rule: Rule; mode: "confirm" | "correct" } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const shown = data.rules.filter((r) => matches(search, r.name, COMPLIANCE_STATES[r.jurisdiction], r.usedIn));
  const waiting = data.rules.filter((r) => r.status !== "confirmed").length;
  return (
    <Section
      title="Rules and rates"
      description="Fees, a tax rate, due dates and state holidays that could not be checked on their official page. Each stays in use, marked Not confirmed, until an owner or admin checks it and confirms or corrects it."
    >
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className={cn("text-sm", waiting ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>
          {waiting ? `${waiting} of ${data.rules.length} rules ${waiting === 1 ? "needs" : "need"} checking.` : `All ${data.rules.length} rules are confirmed.`}
          {!data.canEdit && " The firm's owner or an admin can confirm them."}
        </p>
        <ListSearch value={search} onChange={setSearch} placeholder="Search rules" />
      </div>
      {shown.length === 0 ? (
        <Empty>No rules match your search.</Empty>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {shown.map((r) => (
            <li key={r.key} className="px-4 py-3 text-sm">
              <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1 basis-64">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.usedIn}</p>
                  <p className="text-xs text-muted-foreground">{r.note}</p>
                  {r.officialUrl ? (
                    <a href={r.officialUrl} target="_blank" rel="noreferrer" className="block truncate text-xs text-primary hover:underline">
                      Official page: {r.officialUrl}
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground">No official page on record: use the state's own published list.</p>
                  )}
                  {r.clientFacing && r.status !== "confirmed" && <p className="text-xs text-muted-foreground">Clients see the dates it affects marked as estimates until it is confirmed.</p>}
                </div>
                <div className="w-full shrink-0 sm:w-72">
                  <p className="tabular font-medium">{r.value === null ? "No value" : ruleSettingValueText(r.kind, r.value)}</p>
                  {r.kind === "holiday-list" && Array.isArray(r.value) && r.value.length > 0 && <p className="text-xs text-muted-foreground">{r.value.map((d) => formatDate(d)).join(", ")}</p>}
                  <RuleStatusChip r={r} className="mt-1" />
                  {r.upcoming && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {valueText(r.kind, r.upcoming.value)} from {formatDate(r.upcoming.effectiveFrom)}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.canEdit && r.value !== null && r.status !== "confirmed" && (
                  <Button size="sm" variant="outline" onClick={() => setEditing({ rule: r, mode: "confirm" })}>
                    Confirm
                  </Button>
                )}
                {data.canEdit && (
                  <Button size="sm" variant={r.value === null ? "outline" : "ghost"} onClick={() => setEditing({ rule: r, mode: "correct" })}>
                    {r.value === null ? "Add value" : "Correct"}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setOpen(open === r.key ? null : r.key)} aria-expanded={open === r.key}>
                  {open === r.key ? "Hide history" : `History (${r.history.length})`}
                </Button>
              </div>
              {open === r.key && (
                <div className="mt-2">
                  <History rule={r} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && <ChangeDialog rule={editing.rule} mode={editing.mode} today={data.today} onClose={() => setEditing(null)} />}
    </Section>
  );
}
